import React, { memo, useMemo, Dispatch } from 'react';
import ViewModeConstants, { ViewMode } from '../../constants/ViewModeConstants';
import { connect } from 'react-redux';
import { Box, Paper } from '@mui/material';
import Styles from './LayerStatisticsContainer.module.less';
import LayerStatistics from './LayerStatistics';
import {
  generateStatisticsData,
  generateStatisticsDataForClassification,
} from './LayerStatisticsUtils';
import useLayerStatisticsSource from './sources/useLayerStatisticsSource';
import type {
  FarmRef,
  FieldRef,
  SeasonRef,
  SurveyClassificationValues,
  SurveyReferenceValues,
  YieldDataByField,
} from './sources/LayerStatisticsSourceTypes';

type StoreState = {
  control: { viewMode: ViewMode };
  farm: { farm: FarmRef | null };
  field: {
    date: { date: string | null } | null;
    selectedField: (FieldRef & { size: number }) | null;
    showSatelliteImagery: boolean;
  };
  season: { selectedSeason: SeasonRef | null };
  survey: {
    classificationsEnabled: boolean;
    selectedLayer: string | null;
    selectedSurveyClassificationValues: SurveyClassificationValues;
    selectedSurveyReferenceValues: SurveyReferenceValues | null;
    statisticsEnabled: boolean;
    variationsEnabled: boolean;
  };
  yield: { yieldData: YieldDataByField | null };
};

type LayerStatisticsContainerProps = ReturnType<typeof mapStateToProps> & {
  dispatch: Dispatch<any>;
};

const mapStateToProps = (store: StoreState) => {
  return {
    selectedField: store.field.selectedField,
    date: store.field.date && store.field.date.date,
    farm: store.farm.farm,
    viewMode: store.control.viewMode,
    showSatelliteImagery: store.field.showSatelliteImagery,
    variationsEnabled: store.survey.variationsEnabled,
    classificationsEnabled: store.survey.classificationsEnabled,
    selectedLayer: store.survey.selectedLayer,
    statisticsEnabled: store.survey.statisticsEnabled,
    selectedSurveyReferenceValues: store.survey.selectedSurveyReferenceValues,
    selectedSurveyClassificationValues: store.survey.selectedSurveyClassificationValues,
    selectedSeason: store.season.selectedSeason,
    yieldData: store.yield.yieldData,
  };
};

const LayerStatisticsContainer = ({
  dispatch,
  viewMode,
  showSatelliteImagery,
  selectedLayer,
  classificationsEnabled,
  variationsEnabled,
  selectedField,
  date,
  farm,
  statisticsEnabled,
  selectedSurveyReferenceValues,
  selectedSurveyClassificationValues,
  selectedSeason,
  yieldData,
}: LayerStatisticsContainerProps) => {
  const source = useLayerStatisticsSource({
    layer: selectedLayer,
    viewMode,
    classificationsEnabled,
    variationsEnabled,
    selectedSurveyReferenceValues,
    selectedSurveyClassificationValues,
    date,
    dispatch,
    farm,
    selectedField,
    selectedSeason,
    yieldData,
  });

  const statisticsData = useMemo(() => {
    if (!statisticsEnabled || showSatelliteImagery || !selectedField || !source.isReady) {
      return null;
    }

    if (!source.buckets || !source.values) {
      return null;
    }

    const decimals = 2;
    const buckets = source.buckets.map((bucket) => ({ ...bucket, count: 0 }));

    if (source.useClassificationBuckets) {
      if (!source.classificationData || !source.summaryData) {
        return null;
      }

      return generateStatisticsDataForClassification(
        buckets,
        source.classificationData,
        source.summaryData,
        selectedField,
        decimals
      );
    }

    return generateStatisticsData(
      buckets,
      source.values,
      selectedField,
      decimals,
      source.summaryData
    );
  }, [statisticsEnabled, showSatelliteImagery, selectedField, source]);

  const isSingleField =
    viewMode === ViewModeConstants.ANALYSIS || viewMode === ViewModeConstants.PRESCRIPTION;

  if (showSatelliteImagery || !statisticsEnabled || !isSingleField) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: 180,
        maxWidth: 180,
      }}
    >
      <Paper elevation={2} className={Styles.paper}>
        {statisticsData && <LayerStatistics data={statisticsData} width={130} />}
      </Paper>
    </Box>
  );
};

export default memo(connect(mapStateToProps)(LayerStatisticsContainer));
